import { Suspense, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil';
import EchoClass from 'laravel-echo';
import Pusher from 'pusher-js';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import { StringParam, useQueryParam } from 'use-query-params';
import { Button } from '@mui/material';
import { AuthState } from 'models/AuthModel';
import { CompanyType } from 'models/CompanyModel';
import { CarModel } from 'models/StockModel';
import { VoyageModel, VoyageStatus } from 'models/VoyageModel';
import useCars from 'modules/cars/hooks/useCars';
import useVoyages from 'modules/voyages/hooks/useVoyages';
import app from 'recoil/atoms/app';
import auth from 'recoil/atoms/auth';
import carListFamily from 'recoil/atoms/carListFamily';
import voyageFamily from 'recoil/atoms/voyageFamily';
import Api from 'utils/api';
import { removeItemAtIndex, replaceItemAtIndex } from 'utils/common';


declare global {
    interface Window {
        Echo: EchoClass;
        Pusher: typeof Pusher;
    }
}


Pusher.logToConsole = process.env.NODE_ENV === 'development';
window.Pusher = Pusher;

window.Echo = new EchoClass({
    broadcaster: 'pusher',
    key: process.env.REACT_APP_PUSHER_KEY,
    cluster: process.env.REACT_APP__PUSHER_CLUSTER,
    authorizer: (channel: any, options: any) => {
        return {
            authorize: Api.notifications.auth(channel),
        };
    },
});

type VoyageStatusEvent = {
    id: number,
    old: VoyageStatus,
    new: VoyageStatus,
}

const appId = process.env.REACT_APP_API_UUID;

const LaravelPusherInner = () => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();
    const setApp = useSetRecoilState(app);
    const {getVoyage} = useVoyages();
    const setPendingVoyages = useSetRecoilState(voyageFamily(VoyageStatus.Pending));
    const {getMultiple} = useCars();
    const {enqueueSnackbar} = useSnackbar();
    const navigate = useNavigate();
    const [currentChat] = useQueryParam('chat', StringParam);

    const changeVoyageFamily = useRecoilCallback(({snapshot, set}) => (e: VoyageStatusEvent) => {
        const oldFamily = snapshot.getLoadable(voyageFamily(e.old));
        const newFamily = snapshot.getLoadable(voyageFamily(e.new));

        if (newFamily.state === 'hasValue') {
            const voyage = oldFamily.contents.find((v: VoyageModel) => v.id === e.id)!;
            set(voyageFamily(e.new), old => ([voyage, ...old]));
        }

        if (oldFamily.state === 'hasValue') {
            set(voyageFamily(e.old), old => removeItemAtIndex(old, old.findIndex((v: VoyageModel) => v.id === e.id)));
        }
    }, []);

    const updateCars = useRecoilCallback(({snapshot, set}) => (cars: CarModel[]) => {
        const family = snapshot.getLoadable(carListFamily('pending'));

        if (family.state !== 'hasValue') return;

        set(carListFamily('pending'), old => {
            let temp = [...old];

            cars.forEach((car: CarModel) => {
                const idx = temp.findIndex(i => i.id === car.id);
                temp = idx === -1 ? [car, ...temp] : replaceItemAtIndex(temp, idx, car)
            });

            return temp;
        })
    });

    const removeCars = useRecoilCallback(({snapshot, set}) => ({cars}: { cars: number[] }) => {
        const family = snapshot.getLoadable(carListFamily('pending'));

        if (family.state !== 'hasValue') return;

        set(carListFamily('pending'), old => {
            let temp = [...old];

            cars.forEach(id => {
                temp = removeItemAtIndex(temp, temp.findIndex(i => i.id === id));
            });

            return temp;
        })

    });

    const syncCars = (e: { cars: number[] }) => {
        getMultiple(e.cars)
            .then(res => updateCars(res.data))
    };

    const newMessageNotification = useCallback(({chat, message}: { message: any, chat: any }) => {
        if (message.author.id === my.id || currentChat === chat.code)
            return;

        enqueueSnackbar((
                <div>
                    <strong>{chat.name}</strong><br/>
                    {message.author.name}: {message.text}
                </div>
            ),
            {
                anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'right'
                },
                action: () => (
                    <Button onClick={() => navigate(`/chats?chat=${chat.code}`)}>
                        {t('reply')}
                    </Button>
                ),
            });
        setApp(old => ({...old, chatUnread: old.chatUnread + 1}));
    }, [currentChat, enqueueSnackbar, my.id, navigate]);

    useEffect(() => {
        window.Echo.channel(`${appId}.common`)
            .listen('ClientUpdated', () => {
                setTimeout(() => window.location.reload(), 5 * 60 * 1000);
                enqueueSnackbar(t('new_version_is_available'),
                    {
                        variant: 'warning',
                        persist: true,
                        action: () => (
                            <Button
                                variant="outlined"
                                color="inherit"
                                onClick={() => window.location.reload()}
                            >
                                {t('reload')}
                            </Button>
                        ),
                    });
            });
    }, []);

    useEffect(() => {
        if (my.state !== AuthState.User) return;

        if (my.type === CompanyType.Party) {
            window.Echo.private(`${appId}.party.${my.managedPartyId}`)
                .listen('CarsUpdated', syncCars);

            window.Echo.private(`${appId}.party.${my.managedPartyId}`)
                .listen('CarsDeleted', removeCars);
        } else if (my.type === CompanyType.Shipper || my.type === CompanyType.Master) {
            window.Echo.private(`${appId}.company.${my.company.id}`)
                .listen('CarsUpdated', syncCars);

            window.Echo.private(`${appId}.company.${my.company.id}`)
                .listen('CarsDeleted', removeCars);

            window.Echo.private(`${appId}.company.all`)
                .listen('VoyageStored', (e: any) => {
                    getVoyage(e.id)
                        .then(res => setPendingVoyages(old => [res.data, ...old]));
                })
                .listen('VoyageUpdated', (e: any) => {
                    getVoyage(e.id)
                        .then(res => setPendingVoyages(old => replaceItemAtIndex(old, old.findIndex(i => i.id === e.id), res.data)));
                })
                .listen('VoyageStatusUpdated', changeVoyageFamily)
        } else if (my.type === CompanyType.PortAgent) {
            window.Echo.private(`${appId}.company.1`)
                .listen('CarsUpdated', syncCars);
        }
        // eslint-disable-next-line
    }, [my.state]);

    useEffect(() => {
        if (my.state !== AuthState.User) return;

        window.Echo.private(`${appId}.App.Models.User.${my.id}`)
            .listen('MessageSent', newMessageNotification);

        return () => {
            window.Echo.private(`${appId}.App.Models.User.${my.id}`)
                .stopListening('MessageSent', newMessageNotification)
        }
    }, [my.id, my.state, newMessageNotification]);

    return null;
};

const LaravelPusher = () => {
    return (
        <Suspense fallback={null}>
            <LaravelPusherInner/>
        </Suspense>
    )
};

export default LaravelPusher;
